import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { teamsStore, generateTeamId } from './teamsStore';
import type { TeamDefinition } from '../types';

export function useTeams(): TeamDefinition[] {
  const cachedRef = useRef<TeamDefinition[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = teamsStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newTeams = teamsStore.getSnapshot().context.teams;
    if (cachedRef.current.length === newTeams.length &&
        cachedRef.current.every((t, i) => t.id === newTeams[i]?.id)) {
      return cachedRef.current;
    }
    cachedRef.current = newTeams;
    return newTeams;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useTeamById(teamId: string): TeamDefinition | undefined {
  const teams = useTeams();
  return useMemo(() => teams.find(t => t.id === teamId), [teams, teamId]);
}

export function useTeamsActions() {
  return useMemo(() => ({
    addTeam: (team: TeamDefinition) =>
      teamsStore.send({ type: 'team.add', team }),
    addNewTeam: (name: string, color: string) => {
      const id = generateTeamId(name);
      teamsStore.send({ type: 'team.add', team: { id, name, color } });
      return id;
    },
    updateTeam: (teamId: string, updates: Partial<Omit<TeamDefinition, 'id'>>) =>
      teamsStore.send({ type: 'team.update', teamId, updates }),
    deleteTeam: (teamId: string) =>
      teamsStore.send({ type: 'team.delete', teamId }),
    resetTeams: () =>
      teamsStore.send({ type: 'teams.reset' }),
    importTeams: (teams: TeamDefinition[]) =>
      teamsStore.send({ type: 'teams.import', teams }),
  }), []);
}
